import React, { useState } from 'react';
import api from '../../api';

const ConnectionTest = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  const testConnections = async () => {
    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const response = await api.testConnections();
      setResults(response.data);
    } catch (err) {
      setError({
        message: err.message,
        stack: err.stack,
        details: 'Failed to connect to the test endpoint. Check if the backend is running and accessible.'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDetails = (details) => {
    if (!details || typeof details !== 'object') {
      return details;
    }
    return Object.entries(details).map(([key, value]) => (
      <div key={key} className="ml-4 mb-2">
        <span className="font-semibold text-gray-700">{key}:</span>{' '}
        <span className="text-gray-600">
          {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
        </span>
      </div>
    ));
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h1 className="text-3xl font-bold mb-6 text-gray-800">Database Connection Test</h1>
        <p className="text-gray-600 mb-6">
          This page tests the connection to Redis and MongoDB databases through the backend API.
        </p>

        <button
          onClick={testConnections}
          disabled={loading}
          className={`px-6 py-3 rounded-lg font-semibold text-white transition-colors ${
            loading
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {loading ? 'Testing Connections...' : 'Test Connections'}
        </button>

        {error && (
          <div className="mt-6 p-4 bg-red-50 border-l-4 border-red-500 rounded">
            <h2 className="text-xl font-semibold text-red-800 mb-2">Error</h2>
            <div className="text-red-700 space-y-2">
              <p><strong>Message:</strong> {error.message}</p>
              {error.details && <p><strong>Details:</strong> {error.details}</p>}
              {error.stack && (
                <details className="mt-2">
                  <summary className="cursor-pointer font-semibold">Stack Trace</summary>
                  <pre className="mt-2 p-2 bg-red-100 rounded text-xs overflow-auto">
                    {error.stack}
                  </pre>
                </details>
              )}
            </div>
          </div>
        )}

        {results && (
          <div className="mt-6 space-y-6">
            {/* Redis Results */}
            <div className={`p-5 rounded-lg border-l-4 ${
              results.redis.connected
                ? 'bg-green-50 border-green-500'
                : 'bg-red-50 border-red-500'
            }`}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-2xl font-bold">
                  Redis Connection
                </h2>
                <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                  results.redis.connected
                    ? 'bg-green-200 text-green-800'
                    : 'bg-red-200 text-red-800'
                }`}>
                  {results.redis.connected ? '✓ CONNECTED' : '✗ FAILED'}
                </span>
              </div>

              {results.redis.connected ? (
                <div className="text-green-800 space-y-2">
                  <p className="font-semibold">Connection successful!</p>
                  <div className="mt-3 bg-white p-3 rounded border border-green-200">
                    <h3 className="font-semibold mb-2">Connection Details:</h3>
                    {formatDetails(results.redis.details)}
                  </div>
                </div>
              ) : (
                <div className="text-red-800 space-y-2">
                  <p className="font-semibold">Connection failed!</p>
                  {results.redis.error && (
                    <div className="mt-2 p-3 bg-red-100 rounded border border-red-200">
                      <p className="font-semibold mb-1">Error Message:</p>
                      <p className="font-mono text-sm">{results.redis.error}</p>
                    </div>
                  )}
                  {results.redis.details && Object.keys(results.redis.details).length > 0 && (
                    <div className="mt-3 bg-white p-3 rounded border border-red-200">
                      <h3 className="font-semibold mb-2">Error Details:</h3>
                      {formatDetails(results.redis.details)}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* PostgreSQL Results */}
            <div className={`p-5 rounded-lg border-l-4 ${
              results.postgresql?.connected
                ? 'bg-green-50 border-green-500'
                : 'bg-red-50 border-red-500'
            }`}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-2xl font-bold">
                  PostgreSQL Connection
                </h2>
                <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                  results.postgresql?.connected
                    ? 'bg-green-200 text-green-800'
                    : 'bg-red-200 text-red-800'
                }`}>
                  {results.postgresql?.connected ? '✓ CONNECTED' : '✗ FAILED'}
                </span>
              </div>

              {results.postgresql?.connected ? (
                <div className="text-green-800 space-y-2">
                  <p className="font-semibold">Connection successful!</p>
                  <div className="mt-3 bg-white p-3 rounded border border-green-200">
                    <h3 className="font-semibold mb-2">Connection Details:</h3>
                    {formatDetails(results.postgresql.details)}
                  </div>
                </div>
              ) : (
                <div className="text-red-800 space-y-2">
                  <p className="font-semibold">Connection failed!</p>
                  {results.postgresql?.error && (
                    <div className="mt-2 p-3 bg-red-100 rounded border border-red-200">
                      <p className="font-semibold mb-1">Error Message:</p>
                      <p className="font-mono text-sm">{results.postgresql.error}</p>
                    </div>
                  )}
                  {results.postgresql?.details && Object.keys(results.postgresql.details).length > 0 && (
                    <div className="mt-3 bg-white p-3 rounded border border-red-200">
                      <h3 className="font-semibold mb-2">Error Details:</h3>
                      {formatDetails(results.postgresql.details)}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* MongoDB Results */}
            <div className={`p-5 rounded-lg border-l-4 ${
              results.mongodb.connected
                ? 'bg-green-50 border-green-500'
                : 'bg-red-50 border-red-500'
            }`}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-2xl font-bold">
                  MongoDB Connection
                </h2>
                <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                  results.mongodb.connected
                    ? 'bg-green-200 text-green-800'
                    : 'bg-red-200 text-red-800'
                }`}>
                  {results.mongodb.connected ? '✓ CONNECTED' : '✗ FAILED'}
                </span>
              </div>

              {results.mongodb.connected ? (
                <div className="text-green-800 space-y-2">
                  <p className="font-semibold">Connection successful!</p>
                  <div className="mt-3 bg-white p-3 rounded border border-green-200">
                    <h3 className="font-semibold mb-2">Connection Details:</h3>
                    {formatDetails(results.mongodb.details)}
                  </div>
                </div>
              ) : (
                <div className="text-red-800 space-y-2">
                  <p className="font-semibold">Connection failed!</p>
                  {results.mongodb.error && (
                    <div className="mt-2 p-3 bg-red-100 rounded border border-red-200">
                      <p className="font-semibold mb-1">Error Message:</p>
                      <p className="font-mono text-sm">{results.mongodb.error}</p>
                    </div>
                  )}
                  {results.mongodb.details && Object.keys(results.mongodb.details).length > 0 && (
                    <div className="mt-3 bg-white p-3 rounded border border-red-200">
                      <h3 className="font-semibold mb-2">Error Details:</h3>
                      {formatDetails(results.mongodb.details)}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Raw JSON Output */}
            <details className="mt-6">
              <summary className="cursor-pointer text-lg font-semibold text-gray-700 mb-2">
                View Raw JSON Response
              </summary>
              <pre className="p-4 bg-gray-100 rounded border border-gray-300 text-xs overflow-auto max-h-96">
                {JSON.stringify(results, null, 2)}
              </pre>
            </details>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConnectionTest;

